import {
  all,
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";

import api, { v4ScopeFor, v4ScopeForUuid, v4SessionSafe } from "../../api/V4client";
import { CREATE_V4_ASS_REQUEST } from "../constants/assistanceRequest.constants";
import {
  CLOSE_ALARMS,
  FETCH_V4_ALARMS,
  FETCH_V4_CLOSED_ALARMS,
  REFRESH_V4_ALARMS,
  REFRESH_V4_CLOSED_ALARMS,
  SET_DETAILED_ALARM,
  V4_ALARM_CLOSED_LIVE,
  V4_ALARM_UPDATED_LIVE,
  V4_ALARMS_LOADED,
  V4_CLOSED_ALARMS_LOADED,
} from "../constants/alarm.constants";
import { APP_LOGOUT, WS_DATA_INIT } from "../constants/app.constants";
import { REFRESH_DEVICE_LOCATION } from "../constants/device.constants";
import { latestAlarmPositionFailed, latestAlarmPositionLoaded, refreshV4Alarms, refreshV4ClosedAlarms, setDetailedAlarm, v4AlarmsClosed, v4AlarmsLoaded, v4ClosedAlarmsLoaded, resetV4ClosedAlarms } from "../actions/alarm.actions";
import { openSnackbar } from "../actions/snackbar.actions";
import { selectCurrentUser } from "../selectors/app.selectors";
import { selectAlarms, selectDetailedAlarm, selectLatestAlarmPosition, selectV4ClosedAlarms, selectV4ClosedAlarmsFetched } from "../selectors/alarm.selector";
import { selectDevices } from "../selectors/device.selector";
import Alarm from "../../utils/models/Alarm";
import { CLOSED } from "../../utils/models/AlarmState";
import { SET_SELECTED_ACCOUNT } from "../../containers/AdminPage/constants";
import { mapV4AlarmToV3, mapV4LocationToV3Position } from "../../containers/V4ARC/transforms";
import { situationText } from "../../containers/AlarmPage/situationText";
import { usesAlarmSituations, v4RequestsOnOwnPage } from "../../containers/AlarmPage/functions";
import { isAssistanceRequestAlarm, openRequestsClosedWith } from "../../utils/models/v4AlarmSituation";
import { v4AlarmOriginDevice } from "../../utils/models/v4AlarmOrigin";
import { findDeviceForAlarm } from "../../utils/deviceMerge";
import { alarmLocationViewApplies } from "../../utils/alarmLocationView";
import { consoleWarn } from "../../utils/log";
import { V4AlarmWebSocket, V4_NATIVE_ACCOUNT } from "../../utils/V4AlarmWebSocket";
import { scenarioActionTypes } from "../../utils/models/ScenarioAction";
import { toV4ActionConfig } from "../../containers/V4Scenarios/actionConfigs";
import { toV4ActionType } from "../../containers/V4Scenarios/translations";
import { refuseUnresolvedRecipients, resolveScenarioIdMaps } from "./scenarioV4.saga";
import { selectTeams } from "../selectors/team.selector";
import { selectGeofences } from "../selectors/geofence.selector";
import { createAlarmRequestFrom, hasRecipients, withEveryRecipient } from "../../utils/v4AssistanceRequest";
import { getLocaleInfo } from "../../../i18n";
import { forAnyObject } from "../../utils/models/Scenario";
import { getI18n } from "react-i18next";

// getI18n() is unset until i18next initialises (and in tests): fall back to the default.
const translate = (key, options = {}) => {
  const i18n = getI18n();
  if (typeof i18n?.t === "function") return i18n.t(key, options);
  const { defaultValue = key, ...vars } = options;
  return String(defaultValue).replace(/\{(\w+)\}/g, (match, name) =>
    name in vars ? String(vars[name]) : match
  );
};

const POLL_INTERVAL_MS = 30 * 1000;
// The closed endpoint paginates (default 10). Pull a generous first page so the
// list's client-side "show more" chunking has enough to work through.
const CLOSED_ALARMS_PAGE_SIZE = 100;

let pollerStarted = false;
// The V3 accountId of the scope last used for a successful fetch — re-used by
// the polling loop so we don't have to re-resolve scope each tick.
let lastFetchedAccountId = null;
// Bumped on an account switch: a close landing after it belongs to the old account.
let closedListGeneration = 0;

function* fetchV4AlarmsForAccount(v3AccountId) {
  if (!v3AccountId) {
    lastFetchedAccountId = null;
    yield put(v4AlarmsLoaded([]));
    return;
  }
  // An alarm centre lists the alarms forwarded to its ARC, which live in the
  // customer accounts — `/v1/alarms` would answer for the operator's own account.
  const user = yield select(selectCurrentUser());
  const arcId =
    v3AccountId === V4_NATIVE_ACCOUNT && user?.isV4Arc ? user.v4ArcId : null;
  try {
    let res;
    if (arcId) {
      // The scope header names the ARC here, not an account.
      res = yield call(() => api.alarmsArcList(v4ScopeForUuid(arcId)));
    } else {
      const scope =
        v3AccountId === V4_NATIVE_ACCOUNT
          ? undefined
          : yield call(() => v4ScopeFor(v3AccountId));
      res = yield call(() => api.alarmsList(scope));
    }
    const merged = (res?.data || []).map(mapV4AlarmToV3);
    lastFetchedAccountId = v3AccountId;
    yield put(v4AlarmsLoaded(merged));
  } catch (e) {
    consoleWarn("Failed to fetch V4 alarms", e);
    // An alarm centre has no other account a stale list could leak into, and an
    // emptied console reads as all-clear and silences the siren — keep it.
    if (arcId) return;
    // Account may not exist in V4 yet — clear the list quietly so a stale set
    // doesn't linger after a context switch.
    yield put(v4AlarmsLoaded([]));
  }
}

/**
 * End-user / shadowed-admin path: scope is the current user's accountId.
 * For non-shadowed admins on the admin page, accountId is 0 and we skip —
 * those users pick a sub-account explicitly via SET_SELECTED_ACCOUNT.
 */
function* fetchV4AlarmsForUser() {
  const user = yield select(selectCurrentUser());
  if (user?.isV4Native) {
    yield call(fetchV4AlarmsForAccount, V4_NATIVE_ACCOUNT);
    return;
  }
  if (!user?.accountId) return;
  yield call(fetchV4AlarmsForAccount, user.accountId);
}

function* fetchV4AlarmsForSelectedAccount({ account }) {
  // V4-native keeps its own list; the V3 lookup can't scope a uuid.
  const user = yield select(selectCurrentUser());
  if (user?.isV4Native) return;
  const v3AccountId = account?.get?.("id") ?? account?.id ?? null;
  // New account — reset the stale closed set so it doesn't leak across accounts.
  closedListGeneration += 1;
  yield put(resetV4ClosedAlarms());
  yield call(fetchV4AlarmsForAccount, v3AccountId);
}

// Fetches closed V4 alarms (`/v1/alarms/closed`, the only source) for an account,
// falling back to the last-fetched / current-user account when no id is given.
function* fetchV4ClosedAlarmsForAccount(v3AccountId) {
  const user = yield select(selectCurrentUser());
  // `/v1/alarms/closed` has no ARC form — only `/v1/alarms/arc` takes the ARC as
  // its scope — so it can't list what this ARC closed. Keep the alarms the console
  // closed itself, which also keeps them winning over a list that lags the close.
  if (user?.isV4Arc) {
    yield put(v4ClosedAlarmsLoaded(yield select(selectV4ClosedAlarms())));
    return;
  }
  const target =
    v3AccountId ||
    lastFetchedAccountId ||
    user?.accountId ||
    (user?.isV4Native ? V4_NATIVE_ACCOUNT : null);
  if (!target) {
    yield put(v4ClosedAlarmsLoaded([]));
    return;
  }
  try {
    const scope =
      target === V4_NATIVE_ACCOUNT
        ? undefined
        : yield call(() => v4ScopeFor(target));
    const res = yield call(() =>
      api.alarmsClosedList({ page: 1, limit: CLOSED_ALARMS_PAGE_SIZE }, scope)
    );
    const merged = (res?.data || []).map(mapV4AlarmToV3);
    yield put(v4ClosedAlarmsLoaded(merged));
  } catch (e) {
    consoleWarn("Failed to fetch V4 closed alarms", e);
    yield put(v4ClosedAlarmsLoaded([]));
  }
}

// On-expand fetch (the FETCH action also flips the fetching/spinner flag).
function* fetchV4ClosedAlarms() {
  yield call(fetchV4ClosedAlarmsForAccount, null);
}

// Silent refresh after a close / `alarm.closed` event — skipped unless the list is
// already loaded (an unopened section fetches fresh on expand anyway).
function* refreshV4ClosedAlarmsForAccount({ accountId } = {}) {
  const isActive = yield select(selectV4ClosedAlarmsFetched());
  if (!isActive) return;
  yield call(fetchV4ClosedAlarmsForAccount, accountId || lastFetchedAccountId);
}

/**
 * WS-triggered refresh — `accountId` comes from the websocket manager so it
 * stays aligned with the scope that produced the event, even after an admin
 * has switched accounts.
 */
function* refreshV4AlarmsForAccount({ accountId }) {
  const target = accountId || lastFetchedAccountId;
  if (target) {
    yield call(fetchV4AlarmsForAccount, target);
  } else {
    yield call(fetchV4AlarmsForUser);
  }
}

// Never throws. `alreadyClosedOk`: the backend may have cascade-closed a request with its alarm.
function* closeOneV4Alarm(alarm, { quiet = false, alreadyClosedOk = false } = {}) {
  // V4 alarms carry the owner account UUID — use it directly as scope rather
  // than reverse-resolving via the V3 accountId, which isn't always populated
  // (mobile-app-created devices have no v3-account-id metadata).
  const ownerUuid = alarm.get("v4Data")?.owner_id;
  if (!ownerUuid) {
    consoleWarn("V4 alarm missing owner_id — cannot resolve scope", alarm.toJS());
    if (!quiet) yield put(openSnackbar("Failed to close alarm: missing owner_id"));
    return false;
  }
  try {
    const scope = v4ScopeForUuid(ownerUuid);
    const notes = alarm.get("notes") || "";
    yield call(() => api.alarmsCloseCreate(alarm.id, { notes }, scope));
    return true;
  } catch (e) {
    const status = e?.response?.status;
    const backendMsg = e?.response?.data?.message || "";
    if (alreadyClosedOk && (status === 404 || status === 409)) return true;
    consoleWarn(`Failed to close V4 alarm ${alarm.id}`, e);
    if (quiet) return false;
    // Safety net — the UI normally hides close for exclusive-closing alarms.
    if (status === 403 && backendMsg.includes("alarm_closing_exclusive_to_arc")) {
      yield put(openSnackbar(translate("alarmClosingExclusiveToArc"), "error"));
      return false;
    }
    const detail = backendMsg || e?.message || "unknown error";
    yield put(openSnackbar(`Failed to close alarm${status ? ` (${status})` : ""}: ${detail}`));
    return false;
  }
}

// Locally-closed copy — the open list drops closed alarms, so no refresh can
// supply one. `checked` is the close time.
function markV4AlarmClosed(alarm, closedAt) {
  return alarm.set("state", CLOSED).set("checked", closedAt);
}

function* closeV4AlarmsInner({ alarms }) {
  if (!Array.isArray(alarms)) return;
  const given = alarms.filter((a) => a?.get?.("v4"));
  if (!given.length) return;
  // One stamp for the batch, so its requests read as closed with their alarm.
  const closedAt = Math.floor(Date.now() / 1000);
  const generation = closedListGeneration;

  // Expanded before the open filter, so re-closing a closed alarm still closes its open requests.
  // Without situations (V3 UI, alarm centres) only what was picked closes, as before.
  const user = yield select(selectCurrentUser());
  const situationsOn = usesAlarmSituations(user);
  // The V3 Assistance requests page closes V4 requests here too, so it words them as requests.
  const asRequests = situationsOn || v4RequestsOnOwnPage(user);
  const isRequest = (a) => asRequests && isAssistanceRequestAlarm(a);
  const merged = situationsOn ? yield select(selectAlarms()) : [];
  const givenIds = new Set(given.map((a) => String(a.id)));
  const requestsOf = new Map(
    given.map((root) => [
      root,
      situationsOn
        ? openRequestsClosedWith([root], merged).filter((r) => !givenIds.has(String(r.id)))
        : [],
    ])
  );
  if (!given.some((a) => a.isOpen) && ![...requestsOf.values()].some((rs) => rs.length)) {
    yield call(syncClosedDetailedAlarm, [], given);
    return;
  }

  const closed = [];
  // A failed close keeps its requests open: recipients must still be able to accept.
  const settled = [];
  let failedGivenRequests = 0;
  for (const a of given) {
    if (!a.isOpen) {
      settled.push(a);
      continue;
    }
    const ok = yield call(closeOneV4Alarm, a, { quiet: isRequest(a) });
    if (ok) {
      closed.push(markV4AlarmClosed(a, situationsOn ? closedAt : Math.floor(Date.now() / 1000)));
      settled.push(a);
    } else if (isRequest(a)) {
      failedGivenRequests += 1;
    }
  }

  const requests = settled.flatMap((root) =>
    requestsOf.get(root).map((r) => r.set("notes", root.get("notes") || ""))
  );
  let closedRequests = [];
  if (requests.length) {
    const results = yield all(
      requests.map((r) => call(closeOneV4Alarm, r, { quiet: true, alreadyClosedOk: true }))
    );
    closedRequests = requests
      .filter((r, i) => results[i])
      .map((r) => markV4AlarmClosed(r, closedAt));
  }
  const failedRequests = failedGivenRequests + requests.length - closedRequests.length;

  const allClosed = [...closed, ...closedRequests];
  if (allClosed.length > 0 && generation === closedListGeneration) {
    yield put(v4AlarmsClosed(allClosed));
  }
  yield call(syncClosedDetailedAlarm, allClosed, given);
  // Reconcile through the refresh watchers instead of awaiting here: this worker
  // runs under takeLeading, so anything slow it waits on blocks every later close
  // for the life of the page.
  yield put(refreshV4Alarms(lastFetchedAccountId));
  yield put(refreshV4ClosedAlarms(lastFetchedAccountId));

  // One snackbar: it holds a single message, so a second would replace the first.
  const closedAlarmCount = closed.filter((a) => !isRequest(a)).length;
  const closedRequestCount = closed.length - closedAlarmCount + closedRequests.length;
  const messages = [];
  if (closedAlarmCount > 0) {
    messages.push(closedAlarmCount === 1 ? "Alarm closed" : `${closedAlarmCount} alarms closed`);
  }
  if (closedRequestCount > 0) {
    messages.push(situationText(translate, "requestsClosed", { count: closedRequestCount }));
  }
  if (failedRequests > 0) {
    messages.push(situationText(translate, "requestsNotClosed", { count: failedRequests }));
  }
  if (messages.length > 0) {
    yield put(openSnackbar(messages.join(" · "), failedRequests > 0 ? "error" : "success"));
  }
}

// takeLeading is a `while(true) { take(); call(worker) }` loop: an uncaught throw
// kills the watcher for the life of the page, silently ignoring every later close.
function* closeV4Alarms(action) {
  try {
    yield call(closeV4AlarmsInner, action);
  } catch (e) {
    consoleWarn("V4 alarm close failed unexpectedly", e);
    yield put(openSnackbar("Failed to close alarm: unexpected error"));
  }
}

const V4_ASSISTANCE_REQUEST = toV4ActionType(scenarioActionTypes.assistanceRequest);

// takeEvery, so a second request may follow the first; an escaped error would kill appSaga.
function* createV4AssistanceRequestForAlarm({ alarm, config }) {
  let body = null;
  try {
    const ownerUuid = alarm?.get?.("v4Data")?.owner_id;
    if (!alarm?.id || !ownerUuid) throw new Error("V4 alarm missing id or owner_id");
    // The request belongs to the scoped account, so scope it to the alarm's.
    const scope = v4ScopeForUuid(ownerUuid);
    const user = yield select(selectCurrentUser());
    const picked = withEveryRecipient({ ...config, followUpScenarioId: null }, {
      devices: yield select(selectDevices()),
      teams: yield select(selectTeams()),
      geofences: yield select(selectGeofences()),
      ownerId: ownerUuid,
      accountId: user?.accountId,
    });
    ["devices", "teams", "geofences"].forEach((key) => {
      if (config.recipients?.[key]?.includes?.(forAnyObject) && !picked.recipients[key].length) {
        throw new Error(`no ${key} to expand "all" into`);
      }
    });
    const idMaps = yield call(
      resolveScenarioIdMaps,
      { actions: [{ type: scenarioActionTypes.assistanceRequest, config: picked }] },
      scope,
      { withTrigger: false }
    );
    // Hand-picked recipients only: "All" means whoever V4 can reach.
    yield call(
      refuseUnresolvedRecipients,
      { actions: [{ type: scenarioActionTypes.assistanceRequest, config }] },
      idMaps
    );
    body = createAlarmRequestFrom(toV4ActionConfig(V4_ASSISTANCE_REQUEST, picked, { idMaps }));
    // The translator drops ids with no V4 counterpart; an emptied category would narrow who is asked.
    [["devices", "device_ids"], ["teams", "team_ids"], ["geofences", "geofence_ids"]].forEach(([key, sent]) => {
      if (config.recipients?.[key]?.length && !body.recipients[sent]) throw new Error(`no ${key} exist in V4`);
    });
    if (!hasRecipients(body)) throw new Error("no recipient exists in V4");
    if (body.language) body.language = getLocaleInfo(body.language).lang;
    yield call(() => api.alarmsCreate(body, scope));
    yield put(refreshV4Alarms(lastFetchedAccountId));
  } catch (e) {
    consoleWarn("Failed to create the V4 assistance request", {
      error: e,
      status: e?.response?.status,
      response: e?.response?.data,
      sentBody: body,
    });
    const text = e?.unresolvedRecipients
      ? `${translate("assistanceRequestRecipientsNotInV4", {
          defaultValue: "Not sent: these recipients are not in V4",
        })}: ${e.unresolvedRecipients.join(", ")}`
      : translate("errorDuringAssistanceRequest", {
          defaultValue: "Error during assistance request start",
        });
    yield put(openSnackbar(text, "error"));
  }
}

// Flip the open detail dialog to closed when it's one we just closed — the open
// list no longer carries it, so only this clears loadingDetailedAlarm.
function* syncClosedDetailedAlarm(closedAlarms, given = []) {
  const detailed = yield select(selectDetailedAlarm());
  if (!detailed?.get?.("v4")) return;
  if (detailed.isOpen) {
    const justClosed = closedAlarms.find((a) => a.id === detailed.id);
    if (justClosed) yield put(setDetailedAlarm(justClosed));
    return;
  }
  // Re-closed for its requests: no refresh re-sets it, so its close button would spin for good.
  // This close's copy has the typed notes; a close event's may have got there first.
  if (given.some((a) => a.id === detailed.id)) {
    yield put(setDetailedAlarm(closedAlarms.find((a) => a.id === detailed.id) || detailed));
  }
}

/**
 * Fallback polling for when the V4 alarm websocket can't connect (no V4
 * base URL, ticket fetch failing, or the server dropping us). Started once
 * on the first WS_DATA_INIT; each tick is skipped while the websocket is
 * delivering live events — except for an alarm centre, see below.
 */
function* pollV4Alarms() {
  if (pollerStarted) return;
  pollerStarted = true;
  while (true) {
    yield delay(POLL_INTERVAL_MS);
    const user = yield select(selectCurrentUser());
    if (!user) continue;
    // An alarm centre hears of a new alarm only through its forwarding event, and
    // a refetch that beats the read model to it comes back without it. Keep
    // polling while connected, or that alarm stays hidden for the socket's life.
    if (!user.isV4Arc && V4AlarmWebSocket.inst().isConnected()) continue;
    // Through the refresh watcher, so a tick can't land after a newer live
    // refresh and drop what it added. It falls back to the user's own scope.
    yield put(refreshV4Alarms());
  }
}

function* connectV4WebSocketForUser() {
  const user = yield select(selectCurrentUser());
  if (user?.isV4Native) {
    yield call(() =>
      V4AlarmWebSocket.inst().connectForAccount(V4_NATIVE_ACCOUNT)
    );
    return;
  }
  if (!user?.accountId) return;
  yield call(() => V4AlarmWebSocket.inst().connectForAccount(user.accountId));
}

function* connectV4WebSocketForSelectedAccount({ account }) {
  // V4-native keeps its session scope; the V3 lookup can't scope a uuid.
  const user = yield select(selectCurrentUser());
  if (user?.isV4Native) return;
  const v3AccountId = account?.get?.("id") ?? account?.id ?? null;
  if (!v3AccountId) {
    V4AlarmWebSocket.inst().disconnect();
    return;
  }
  yield call(() => V4AlarmWebSocket.inst().connectForAccount(v3AccountId));
}

function* disconnectV4WebSocket() {
  yield call(() => V4AlarmWebSocket.inst().disconnect());
}

function* refreshDetailedV4Alarm({ alarms }) {
  const detailed = yield select(selectDetailedAlarm());
  if (!detailed?.get?.("v4")) return;
  const next = (alarms || []).find((a) => a.id === detailed.id);
  if (!next) return;
  const refreshed = new Alarm(next);
  // V4 alarms never reopen: an open copy is a lagging list.
  if (!detailed.isOpen && refreshed.isOpen) return;
  yield put(setDetailedAlarm(refreshed));
}

// `alarm.{id}.closed`, ahead of the socket's refetch. Guarded: an escaped error kills appSaga.
function* closeV4AlarmLive({ alarmId }) {
  try {
    const held = (yield select(selectAlarms())).find(
      (a) => a?.get?.("v4") && String(a.id) === String(alarmId)
    );
    if (!held?.isOpen) return;
    const closed = markV4AlarmClosed(held, Math.floor(Date.now() / 1000));
    yield put(v4AlarmsClosed([closed]));
    yield call(syncClosedDetailedAlarm, [closed]);
  } catch (e) {
    consoleWarn("Applying a V4 alarm close event failed", e);
  }
}

// `alarm.{id}.updated` for an alarm only the closed list holds. Guarded: an escaped error kills appSaga.
function* refreshClosedV4AlarmOnUpdate({ alarmId, accountId }) {
  try {
    const closed = yield select(selectV4ClosedAlarms());
    if (closed.some((a) => String(a.id) === String(alarmId))) {
      yield put(refreshV4ClosedAlarms(accountId));
    }
  } catch (e) {
    consoleWarn("Applying a V4 alarm update event failed", e);
  }
}

// Refreshes re-set the detailed alarm, the ARC's every 30 s.
const LATEST_POSITION_MAX_AGE_MS = 25 * 1000;
// One read at a time per alarm.
let latestPositionRead = { alarmId: null, at: 0, pending: false, again: false };

// Newest fix of the detailed alarm's device when the list lacks it (the ARC's customer devices).
function* fetchLatestAlarmPosition({ force = false } = {}) {
  // A throw here would kill the root saga.
  try {
    const alarm = yield select(selectDetailedAlarm());
    const user = yield select(selectCurrentUser());
    if (!alarmLocationViewApplies(user, alarm)) return;
    if (findDeviceForAlarm(yield select(selectDevices()), alarm)) return;
    const v4Data = alarm.get("v4Data");
    const device = v4AlarmOriginDevice(v4Data);
    const owner = device?.owner_id || v4Data?.owner_id;
    if (!device?.id || !owner) return;
    const alarmId = String(alarm.id);
    const kept = yield select(selectLatestAlarmPosition());
    if (kept?.alarmId === alarmId && kept.failed) return;
    const read = latestPositionRead.alarmId === alarmId ? latestPositionRead : null;
    if (read?.pending) {
      // Read once more after the pending one.
      if (force) read.again = true;
      return;
    }
    // Leaving the alarm cleared its read.
    if (!force && read && kept?.alarmId === alarmId && Date.now() - read.at < LATEST_POSITION_MAX_AGE_MS) return;
    latestPositionRead = { alarmId, at: read?.at || 0, pending: true, again: false };
    let again = false;
    const settle = (at) => {
      if (latestPositionRead.alarmId !== alarmId) return;
      again = latestPositionRead.again;
      latestPositionRead = { alarmId, at, pending: false, again: false };
    };
    try {
      // Session-safe: a refusal is no expired session.
      const res = yield call(() =>
        api.locationsDetail(device.id, { limit: 1 }, v4SessionSafe(v4ScopeForUuid(owner)))
      );
      settle(Date.now());
      yield put(latestAlarmPositionLoaded(alarmId, mapV4LocationToV3Position(res?.data?.[0] || null)));
    } catch (e) {
      settle(0);
      const status = e?.response?.status;
      consoleWarn("Failed to read the alarm device's location", e);
      // Only a refusal stops the reads.
      if (status === 401 || status === 403) yield put(latestAlarmPositionFailed(alarmId));
    }
    if (again) yield call(fetchLatestAlarmPosition, { force: true });
  } catch (e) {
    consoleWarn("Alarm device location read failed unexpectedly", e);
  }
}

function* fetchLatestAlarmPositionOnLocation({ uuid }) {
  const alarm = yield select(selectDetailedAlarm());
  if (!uuid || v4AlarmOriginDevice(alarm?.get?.("v4Data"))?.id !== uuid) return;
  yield call(fetchLatestAlarmPosition, { force: true });
}

// Moves the panel off a hidden V3 copy (deep link, or opened before the V4 alarm
// landed) onto its V4 alarm, so it's handled and closed there.
function* followV4AlarmOfV3Detail() {
  const detailed = yield select(selectDetailedAlarm());
  if (!detailed?.get || detailed.get("v4")) return;
  const alarms = yield select(selectAlarms());
  const v4Alarm = alarms.find(
    (a) => a.get("v4") && !!a.get("v3AlarmId") && String(a.get("v3AlarmId")) === String(detailed.id)
  );
  if (v4Alarm) yield put(setDetailedAlarm(v4Alarm));
}

export const alarmV4Sagas = [
  takeLatest(WS_DATA_INIT, fetchV4AlarmsForUser),
  takeLatest(SET_SELECTED_ACCOUNT, fetchV4AlarmsForSelectedAccount),
  takeLatest(FETCH_V4_ALARMS, fetchV4AlarmsForUser),
  takeLatest(FETCH_V4_CLOSED_ALARMS, fetchV4ClosedAlarms),
  takeLatest(REFRESH_V4_CLOSED_ALARMS, refreshV4ClosedAlarmsForAccount),
  takeLatest(REFRESH_V4_ALARMS, refreshV4AlarmsForAccount),
  takeLeading(CLOSE_ALARMS, closeV4Alarms),
  takeEvery(CREATE_V4_ASS_REQUEST, createV4AssistanceRequestForAlarm),
  takeLatest(V4_ALARMS_LOADED, refreshDetailedV4Alarm),
  takeEvery(V4_ALARM_CLOSED_LIVE, closeV4AlarmLive),
  takeEvery(V4_ALARM_UPDATED_LIVE, refreshClosedV4AlarmOnUpdate),
  takeLatest([SET_DETAILED_ALARM, V4_ALARMS_LOADED, V4_CLOSED_ALARMS_LOADED], followV4AlarmOfV3Detail),
  takeEvery(SET_DETAILED_ALARM, fetchLatestAlarmPosition),
  takeEvery(REFRESH_DEVICE_LOCATION, fetchLatestAlarmPositionOnLocation),
  takeLatest(WS_DATA_INIT, pollV4Alarms),
  takeLatest(WS_DATA_INIT, connectV4WebSocketForUser),
  takeLatest(SET_SELECTED_ACCOUNT, connectV4WebSocketForSelectedAccount),
  takeLatest(APP_LOGOUT, disconnectV4WebSocket),
];
